import type { DialogProps } from "@radix-ui/react-dialog";
import { Command as CommandPrimitive } from "cmdk";
import { Search } from "lucide-react";
import type * as React from "react";
import { useRef } from "react";

import { cn } from "../utils";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogPortal,
} from "./modal";

const Command = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      "flex h-full w-full flex-col overflow-hidden rounded-md bg-white text-black",
      className,
    )}
    {...props}
  />
);
Command.displayName = CommandPrimitive.displayName;

interface ICommandDialogProps extends DialogProps {
  variant?: "default" | "simple";
}

const CommandDialog = ({
  children,
  variant = "default",
  ...props
}: ICommandDialogProps) => {
  if (variant === "simple") {
    return (
      <ResponsiveDialog {...props}>
        <ResponsiveDialogPortal>
          <ResponsiveDialogContent className="overflow-hidden p-0">
            {children}
          </ResponsiveDialogContent>
        </ResponsiveDialogPortal>
      </ResponsiveDialog>
    );
  }

  return (
    <ResponsiveDialog {...props}>
      <ResponsiveDialogContent className="overflow-hidden p-0">
        <Command className="**:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-gray-600 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 **:[[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 **:[[cmdk-input]]:h-12 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};

const CommandInput = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) => (
  // shrink-0: in a height-capped flex column (the mobile select sheet) the
  // sibling list is `flex-1 min-h-0`, so the flex algorithm reclaims overflow
  // from this row instead — squashing the search field.
  <div className="flex shrink-0 items-center border-b px-3">
    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        "flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-gray-500 placeholder:font-normal disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  </div>
);

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className)}
    {...props}
  />
);

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = ({
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="py-6 text-center text-sm"
    {...props}
  />
);

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      "overflow-hidden p-1 text-black **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-gray-600",
      className,
    )}
    {...props}
  />
);

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 h-px bg-gray-200", className)}
    {...props}
  />
);
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const COMMAND_ITEM_CLASS =
  "relative flex cursor-default gap-2 select-none items-center rounded-xs px-2 py-1.5 text-sm outline-hidden data-[disabled=true]:pointer-events-none data-[selected=true]:bg-gray-100 data-[selected=true]:text-black data-[disabled=true]:opacity-50 has-svg:pointer-events-none has-svg:size-4 has-svg:shrink-0";

const isModifiedClick = (event: React.MouseEvent) =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

interface ICommandItemProps
  extends React.ComponentProps<typeof CommandPrimitive.Item> {
  // Makes the row a real anchor, so a modified click (ctrl/cmd/shift/alt) or a middle click opens
  // the target in a new tab. Plain click and Enter still go through `onSelect` (typically SPA
  // navigation) — `onSelect` is skipped for a modified click so the current tab doesn't also
  // navigate. Middle-click never reaches `onClick`, so the browser handles it natively.
  href?: string;
}

const CommandItem = ({
  className,
  ref,
  href,
  onSelect,
  ...props
}: ICommandItemProps) => {
  const wasModifiedClick = useRef(false);
  const itemClassName = cn(COMMAND_ITEM_CLASS, className);

  if (!href) {
    return (
      <CommandPrimitive.Item
        ref={ref}
        className={itemClassName}
        onSelect={onSelect}
        {...props}
      />
    );
  }

  const { children, ...rest } = props;
  return (
    <CommandPrimitive.Item
      ref={ref}
      className={itemClassName}
      {...rest}
      asChild
      onSelect={(value) => {
        if (wasModifiedClick.current) {
          wasModifiedClick.current = false;
          return;
        }
        onSelect?.(value);
      }}
    >
      <a
        href={href}
        tabIndex={-1}
        onClickCapture={(event) => {
          wasModifiedClick.current = isModifiedClick(event);
          if (!wasModifiedClick.current) {
            event.preventDefault();
          }
        }}
      >
        {children}
      </a>
    </CommandPrimitive.Item>
  );
};

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn("ml-auto text-xs tracking-widest text-gray-600", className)}
      {...props}
    />
  );
};
CommandShortcut.displayName = "CommandShortcut";

const CommandLoading = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Loading>) => (
  <CommandPrimitive.Loading
    ref={ref}
    className={cn("p-1", className)}
    {...props}
  />
);
CommandLoading.displayName = CommandPrimitive.Loading.displayName;

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  CommandSeparator,
  CommandShortcut,
};
